import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  GetResourceSearchResultsItem,
  GetResourceSearchResultsRequest,
} from "@/api/getResourceSearchResults/types";
import { AppDispatch, AppThunk } from "@/app/store";
import { newsAlgoliaService } from "@/modules/hub/features/News/services/newsAlgoliaService";

interface NewsState {
  resources: GetResourceSearchResultsItem[];
  page: number;
  hasMore: boolean;
  totalCount: number;
  isFetching: boolean;
  pendingRequest: "search" | "pagination" | null;
  lastFetchParams: {
    searchTerm?: string;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: string;
  } | null;
}

const initialState: NewsState = {
  resources: [],
  page: 1,
  hasMore: true,
  totalCount: 0,
  isFetching: false,
  pendingRequest: null,
  lastFetchParams: null,
};

const newsSlice = createSlice({
  name: "news",
  initialState,
  reducers: {
    setResources: (
      state,
      action: PayloadAction<GetResourceSearchResultsItem[]>,
    ) => {
      state.resources = action.payload;
    },
    appendResources: (
      state,
      action: PayloadAction<GetResourceSearchResultsItem[]>,
    ) => {
      // Filter out duplicates based on resource ID
      const existingIds = new Set(state.resources.map((r) => r.id));
      const newResources = action.payload.filter((r) => !existingIds.has(r.id));
      state.resources = [...state.resources, ...newResources];
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setIsFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    setPendingRequest: (
      state,
      action: PayloadAction<"search" | "pagination" | null>,
    ) => {
      state.pendingRequest = action.payload;
    },
    setLastFetchParams: (
      state,
      action: PayloadAction<NewsState["lastFetchParams"]>,
    ) => {
      state.lastFetchParams = action.payload;
    },
    resetState: (state) => {
      Object.assign(state, initialState);
    },
  },
});

export const fetchResources =
  (params: GetResourceSearchResultsRequest): AppThunk =>
  async (dispatch: AppDispatch, getState) => {
    const state = getState().news;

    // Prevent concurrent search requests
    if (state.pendingRequest === "search") {
      return;
    }

    dispatch(setIsFetching(true));
    dispatch(setPendingRequest("search"));

    try {
      const result = await newsAlgoliaService.searchNews(params);

      dispatch(setResources(result.resources));
      dispatch(setTotalCount(result.totalCount));
      dispatch(setPage(params.page || 1));
      dispatch(setLastFetchParams(params));

      const hasMore = result.resources.length >= (params.pageSize || 10);
      dispatch(setHasMore(hasMore));
    } catch (error) {
      console.error("Error fetching news:", error);
      dispatch(setHasMore(false));
    } finally {
      dispatch(setIsFetching(false));
      dispatch(setPendingRequest(null));
    }
  };

export const fetchMoreResources =
  (params: GetResourceSearchResultsRequest): AppThunk =>
  async (dispatch: AppDispatch, getState) => {
    const state = getState().news;

    // Prevent concurrent pagination requests
    if (state.pendingRequest === "pagination") {
      return;
    }

    dispatch(setIsFetching(true));
    dispatch(setPendingRequest("pagination"));

    try {
      const result = await newsAlgoliaService.searchNews(params);

      dispatch(appendResources(result.resources));
      dispatch(setPage(params.page || 1));
      dispatch(setLastFetchParams(params));

      const hasMore = result.resources.length >= (params.pageSize || 10);
      dispatch(setHasMore(hasMore));
    } catch (error) {
      console.error("Error loading more news:", error);
      dispatch(setHasMore(false));
    } finally {
      dispatch(setIsFetching(false));
      dispatch(setPendingRequest(null));
    }
  };

export const {
  setResources,
  appendResources,
  setPage,
  setHasMore,
  setTotalCount,
  setIsFetching,
  setPendingRequest,
  setLastFetchParams,
  resetState,
} = newsSlice.actions;

export default newsSlice.reducer;
